import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton as _LoadingButton } from '@mui/lab';
import { Box, Grid, Typography, styled } from "@mui/material";
import { forwardRef } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IMaskInput } from 'react-imask';
import { Link, useNavigate } from "react-router-dom";
import { TypeOf, object, string } from "zod";
import imgRecover from '../../assets/carta-recover.png';
import { useRegisterUserMutation } from "../../redux/api/authApi";
import MyToastShow from "../Layout/MyToast";
import FormInput from "./FormInput";

const LoadingButton = styled(_LoadingButton)`
  padding: 0.6rem 0;
  font-weight: 500;
  &:hover {
    transform: translateY(-2px);
  }
`;

interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}

const CuitMask = forwardRef<HTMLInputElement, CustomProps>(
    function CuitMask(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                mask="##-########-#"
                definitions={{
                    '#': /[0-9]/,
                }}
                inputRef={ref}
                onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
                overwrite
            />
        );
    },
);







const registerSchema = object({
    nombre: string().min(1, 'Ingresá tu nombre').max(100),
    apellido: string().min(1, 'Ingresá tu apellido').max(100),
    cuit: string()
        .min(1, 'Ingresá tu CUIT')
        .max(40)
        .refine((value) => value.length === 13, { message: 'El CUIT debe tener 11 digitos' }),
    telefono: string().min(1, 'Ingresá tu telefono').max(100),
    email: string()
        .min(1, 'Necesitas ingresar un email')
        .email('El email no es valido'),
    password: string()
        .min(1, 'Necesitas ingresar una contraseña')
        .min(8, 'La contraseña debe tener al menos 8 caracteres')
        .max(32, 'La contraseña no puede tener más de 32 caracteres')
        .refine((value) => /[A-Z]/.test(value), { message: 'La contraseña debe contener al menos una mayúscula' })
        .refine((value) => /[a-z]/.test(value), { message: 'La contraseña debe contener al menos una minúscula' }),
    passwordConfirm: string().min(1, 'Confirmá tu contraseña'),
    empresa: string().optional(),
}).refine((data) => data.password === data.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Las contraseñas no coinciden',
});

export type RegisterInput = TypeOf<typeof registerSchema>;

const MAIL_VERIFICATION_ACTIVE = false

const messages = {
    confirmation: 'Hemos enviado un correo a tu cuenta de email para que puedas confirmar tu cuenta.',
}

const RegisterForm = () => {
    const navigate = useNavigate();

    const methods = useForm<RegisterInput>({
        resolver: zodResolver(registerSchema),
    });

    // 👇 Calling the Register Mutation
    const [registerUser, { isLoading, isSuccess }] =
        useRegisterUserMutation();


    const {
        handleSubmit,
    } = methods;


    const onSubmitHandler: SubmitHandler<RegisterInput> = (values) => {
        if (!MAIL_VERIFICATION_ACTIVE) {
            MyToastShow({ message: `${values.nombre} te registraste con éxito! Iniciá sesión para continuar`, type: 'success' })
            navigate('/login');
        }


        registerUser(values).unwrap().then((res) => {
            // MyToastShow({ message: `${values.nombre} Revisá tu mail!`, type: 'success' })

        }).catch((err) => {
            MyToastShow({ message: err?.data || `Error al crear el usuario`, type: 'error' })
        });

    };

    return (

        <FormProvider {...methods}>
            <Box
                component='form'
                onSubmit={handleSubmit(onSubmitHandler)}
                noValidate
                autoComplete='off'
                maxWidth='28rem'
                width='100%'
                sx={{
                    p: { xs: '1rem', sm: '2rem' },
                    borderRadius: 2,
                    backgroundColor: '',

                }}
            >


                {isSuccess ? <>
                    <div style={{ width: '100%', display: 'flex' }}>
                        <img src={imgRecover} alt='imagen carta' style={{ width: '30%', marginBottom: '1rem', margin: '0 auto' }} />
                    </div>

                    <Typography sx={{ mb: '1rem', textAlign: 'center' }}>
                        {messages.confirmation}
                    </Typography>
                </> : <>


                    <Grid container justifyContent={'space-around'} spacing={3}>
                        <Grid item xs={6}>
                            <FormInput name='nombre' label='Nombre/s' type='text' />
                        </Grid>
                        <Grid item xs={6}>
                            <FormInput name='apellido' label='Apellido/s' type='text' />
                        </Grid>
                        <Grid item xs={6}>
                            <FormInput name='cuit' label='Cuit' type='text'
                                inputComponent={CuitMask as any}

                            />
                            {/* <CuitInput name='cuit' label='Cuit' type='text' /> */}
                        </Grid>
                        <Grid item xs={6}>
                            <FormInput name='telefono' label='telefono' type='text'
                            />
                        </Grid>
                    </Grid>
                    <FormInput name='email' label='Email' type='email' />
                    <FormInput name='empresa' label='Empresa' type='text' />
                    <Grid container justifyContent={'space-around'} spacing={3}>
                        <Grid item xs={6}>
                            <FormInput name='password' label='Contraseña' type='password' />
                        </Grid>
                        <Grid item xs={6}>
                            <FormInput name='passwordConfirm' label='Confirmar Contraseña' type='password' />
                        </Grid>
                    </Grid>
                    <LoadingButton
                        variant='contained'
                        sx={{ mt: 1 }}
                        fullWidth
                        disableElevation
                        type='submit'
                        loading={isLoading}
                    >
                        Registrarse
                    </LoadingButton>


                    <Typography sx={{ fontSize: '0.9rem', mt: '1rem' }}>
                        Ya tenés una cuenta?  <Link to={'/login'}>Ingresá</Link>
                    </Typography>
                </>}
            </Box>
        </FormProvider>

    );
}

export default RegisterForm;